function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
import { ValidationFormat } from '@wix/ambassador-forms-v4-form/types';
import { getCountryByShortKey } from '@wix/locale-dataset-javascript';
import { applyValidationProperties } from './apply-validation-properties';
import { applyViewProperties } from './apply-view-properties';
import { ADDRESS_FORM_ID_BY_COUNTRY, DEFAULT_COUNTRY } from './constants';
import { COUNTRY_CODES } from './country-codes';
import { getCountryField } from './get-country-field';
import { mapSubdivisionReplacedBy } from './map-subdivision-replaced-by';
export class MultilineAddressToolkit {
    getFormByCountry(country, rootFormField) {
        this.assertMultiLineAddressField(rootFormField);
        const addressForm = country && this.addressFormByCountry[country] ? this.addressFormByCountry[country] : this.addressFormByCountry[DEFAULT_COUNTRY];
        if (!addressForm) {
            throw new Error(`No form suitable fo country: ${country}`);
        }
        return rootFormField ? this.addCountryField(this.addSubdivisions(this.applyRootFormSettingsOverrides(addressForm, rootFormField), country), rootFormField) : addressForm;
    }
    applyRootFormSettingsOverrides(addressForm, rootFormField) {
        const modifications = rootFormField ? [
            applyViewProperties(rootFormField.view),
            applyValidationProperties(rootFormField.validation?.predefined?.multilineAddressOptions)
        ] : [];
        return modifications.reduce((acc, func)=>func(acc), addressForm);
    }
    assertMultiLineAddressField(rootFormField) {
        if (rootFormField && rootFormField.validation?.predefined?.format !== ValidationFormat.MULTILINE_ADDRESS) {
            throw new Error(`Only field ${ValidationFormat.MULTILINE_ADDRESS} is supported`);
        }
    }
    mapFormsByCountry(formIdByCountryMap, addressForms) {
        const formsById = addressForms.reduce((acc, form)=>{
            acc[form.id] = form;
            return acc;
        }, {});
        return Object.fromEntries(Object.entries(formIdByCountryMap).map(([country, formId])=>{
            const form = formsById[formId];
            if (form) {
                return [
                    country,
                    form
                ];
            } else {
                throw new Error(`Missing form template for country ${country}`);
            }
        }));
    }
    getCountry(value) {
        if (value && typeof value === 'object' && 'country' in value) {
            return value.country;
        }
        return DEFAULT_COUNTRY;
    }
    constructor(addressForms, addressFormIdByCountry = ADDRESS_FORM_ID_BY_COUNTRY){
        _define_property(this, "addressFormByCountry", void 0);
        /**
   * Normalizes empty values in submission to empty objects
   * This is needed to pass validation when address is required
   * @param submission - submission to normalize
   * @param form - form to get fields from
   */ _define_property(this, "normalizeEmptyValues", (submission, form)=>{
            return Object.keys(submission).reduce((acc, key)=>{
                const field = form.fields?.find((_field)=>_field.target === key);
                if (field?.validation?.required && !field?.hidden && field?.validation?.predefined?.format === ValidationFormat.MULTILINE_ADDRESS) {
                    acc[key] = submission[key] || {};
                } else {
                    acc[key] = submission[key];
                }
                return acc;
            }, {});
        });
        _define_property(this, "addCountryField", (form, rootFormField)=>{
            return {
                ...form,
                fields: [
                    getCountryField(rootFormField.validation?.required && !rootFormField?.hidden, rootFormField.validation?.predefined?.multilineAddressOptions),
                    ...form.fields ?? []
                ]
            };
        });
        _define_property(this, "addSubdivisions", (form, countryCode)=>{
            if (!countryCode) {
                return form;
            }
            const country = getCountryByShortKey(countryCode);
            const countryCodeAlpha3 = countryCode && countryCode in COUNTRY_CODES ? COUNTRY_CODES[countryCode] : undefined;
            const subdivisions = mapSubdivisionReplacedBy(country?.subdivisions?.list || [], countryCodeAlpha3);
            if (!subdivisions?.length) {
                return form;
            }
            return {
                ...form,
                fields: form.fields?.map((field)=>{
                    if (field?.target === 'subdivision') {
                        return {
                            ...field,
                            view: {
                                ...field.view,
                                options: subdivisions.map((subdivision)=>({
                                        value: subdivision.key,
                                        label: subdivision.displayName
                                    }))
                            },
                            validation: {
                                ...field.validation,
                                string: {
                                    ...field.validation?.string,
                                    enum: subdivisions.map((subdivision)=>subdivision.key)
                                }
                            }
                        };
                    }
                    return field;
                })
            };
        });
        /**
   * Adds hardcoded country field, which is needed to build validation.
   * Address forms themselves not contain country field
   * @param rootFormField - root form field
   * @param submissionValue - value of submission, to get country info
   */ _define_property(this, "getFormForValidation", (rootFormField, submissionValue)=>{
            const country = this.getCountry(submissionValue);
            return this.getFormByCountry(country, rootFormField);
        });
        this.addressFormByCountry = this.mapFormsByCountry(addressFormIdByCountry, addressForms);
    }
}

//# sourceMappingURL=multiline-address-toolkit.js.map